import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Action, Alert, Modal, NumberInput } from 'corso-admin-ui';
import { CrewClaimResolutionMethodEnum, CrewMerchantUi } from 'corso-types';
import { useForm } from 'react-hook-form';
import api from '~/api';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import { useClaimLedgerSummary } from '~/features/claimReview/hooks/useClaimLedgerSummary';
import { useCurrency } from '~/hooks/useCurrency';
import { useStoreId } from '~/providers/StoreUserProvider';

type ClaimUpdateFees = CrewMerchantUi.ClaimUpdateRefundHandlingFee;
const claimUpdateFees = CrewMerchantUi.claimUpdateRefundHandlingFeeSchema;

export function useShowEditClaimFee() {
  const claim = useClaim();
  return (
    claim.claimLineItems.filter(
      (claimLineItem) =>
        claimLineItem.resolutionMethodEnum ===
          CrewClaimResolutionMethodEnum.refund ||
        claimLineItem.resolutionMethodEnum === 'Gift_Card' ||
        claim.feeApplied !== 0,
    ).length > 0 && claim.claimRollup.code !== 'Completed'
  );
}

export default function EditClaimFee({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const claim = useClaim();

  const hasLineItemFees = claim.claimLineItems.some(
    (cli) => cli.fees && cli.fees.length,
  );

  const currentRefundHandlingFee = claim.feeApplied;
  const claimLedgerSummary = useClaimLedgerSummary();
  const storeId = useStoreId();
  const { currencyCode, currencySymbol } = useCurrency();

  const { mutateAsync: updateClaimFees, isPending } = useMutation({
    mutationFn: (claimFeesUpdate: ClaimUpdateFees) =>
      api.store(storeId).claim(`${claim.id}`).updateFees(claimFeesUpdate),
    onSettled: async () =>
      Promise.all([claim.refetch(), claimLedgerSummary.refetch()]),
  });

  const formId = 'edit-claim-fees-form';
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm({
    resolver: zodResolver(claimUpdateFees),
    defaultValues: {
      feeApplied: currentRefundHandlingFee,
      exchangeOrderShippingAmountApplied:
        claim.exchangeOrderShippingAmountApplied,
      returnShippingAmountApplied: claim.returnShippingAmountApplied,
    } satisfies ClaimUpdateFees,
  });

  const closeAndReset = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      title="Edit Fees"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Save
          </Action>
        </>
      }
    >
      <form
        id={formId}
        // oxlint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(
          async ({
            feeApplied,
            exchangeOrderShippingAmountApplied,
            returnShippingAmountApplied,
          }) => {
            await updateClaimFees({
              feeApplied,
              exchangeOrderShippingAmountApplied,
              returnShippingAmountApplied,
            });
            onClose();
          },
        )}
        className="flex flex-col gap-4"
      >
        {hasLineItemFees && (
          <Alert variant="info" heading="Line Item Fees">
            Fees applied to individual items are edited on their line item, not
            here.
          </Alert>
        )}
        <NumberInput
          id="refund-handling-fee"
          label={claim.feeName ?? 'Handling Fee'}
          step="0.01"
          details="The amount that was collected, or will be deducted at finalization."
          addon={{
            /**
             *  not ideal as symbol placement depends on locale
             * and in some locales the symbol is not used at all
             * e.g. es-MX, es-AR the Euro symbol is not used
             * but instead display prefixed with `EUR`
             */
            insideStart: currencySymbol,
            insideEnd: currencyCode,
          }}
          required
          {...register('feeApplied', {
            valueAsNumber: true,
          })}
          error={errors.feeApplied?.message}
        />

        <NumberInput
          id="exchange-order-shipping-amount"
          label="Exchange Order Shipping Amount"
          step="0.01"
          details="The amount that was collected, or will be deducted at finalization for shipping on the exchange order."
          addon={{
            /**
             *  not ideal as symbol placement depends on locale
             * and in some locales the symbol is not used at all
             * e.g. es-MX, es-AR the Euro symbol is not used
             * but instead display prefixed with `EUR`
             */
            insideStart: currencySymbol,
            insideEnd: currencyCode,
          }}
          {...register('exchangeOrderShippingAmountApplied', {
            valueAsNumber: true,
          })}
          error={errors.exchangeOrderShippingAmountApplied?.message}
        />
        <NumberInput
          id="return-shipping-amount"
          label="Return Shipping Amount"
          step="0.01"
          details="The amount that was collected, or will be deducted at finalization for shipping on the return."
          addon={{
            /**
             *  not ideal as symbol placement depends on locale
             * and in some locales the symbol is not used at all
             * e.g. es-MX, es-AR the Euro symbol is not used
             * but instead display prefixed with `EUR`
             */
            insideStart: currencySymbol,
            insideEnd: currencyCode,
          }}
          {...register('returnShippingAmountApplied', {
            valueAsNumber: true,
          })}
          error={errors.returnShippingAmountApplied?.message}
        />
      </form>
    </Modal>
  );
}
